import { useUnit } from 'effector-react';
import _, { isBoolean } from 'lodash';
import $$api from 'models/api';
import $$dashboard from 'models/dashboard';
import $$ep from 'models/ep';
import { $isDashboard, $pathname } from 'models/location';
import $$trustFunding from 'models/pages/trust-funding';
import { navigateFx } from 'models/router-gate';
import { $global } from 'models/store-global';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import SecureButton from 'components/Dashboard/EstatePlanCard/ExecutingSubcard/SecureButton/SecureButton';
import ViewDocumentsButton from 'components/Dashboard/EstatePlanCard/ExecutingSubcard/ViewDocumentsButton/ViewDocumentsButton';
import ModalAssessment from 'components/Modals/ModalAssessment';
import DashboardScansModal from 'components/PopUps/DashboardScansModal';
import ShippingDetailsCollectModal from 'components/PopUps/ShippingDetailsCollectModal';
import UpdateParticipantModal from 'components/PopUps/UpdateParticipantModal';
import DashboardHeader from 'components/pages/Dashboard/DashboardHeader';
import Menu from 'components/pages/Dashboard/Menu';
import { DashboardStyles } from 'components/pages/Dashboard/common/CommunityPageStyles';
import ReferalCard from 'components/pages/Dashboard/common/ReferalCard';
import CompletePage from 'components/pages/Dashboard/complete/CompletePage';
import DocumentsPage from 'components/pages/Dashboard/documents/DocumentsPage';
import InitialPage from 'components/pages/Dashboard/initial/InitialPage';
import CommunityParticipantsPage from 'components/pages/Dashboard/participants/ParticipantsPage';
import ProgressPage from 'components/pages/Dashboard/progress/ProgressPage';
import CommunityAssignedRolesPage from 'components/pages/Dashboard/roles/AssignedRolesPage';
import GiftTrustPage from 'components/pages/GiftTrustPage/GiftTrustPage';
import LoadingPage from 'components/pages/LoadingPage/LoadingPage';
import TrustFundingPage from 'components/pages/TrustFundingPage/TrustFundingPage';

import EstatePlanService from 'services/EstatePlanService';
import FeatureThresholdService from 'services/FeatureThresholdService';

const FirstMenuBlockPages = () => {
    const answersLoaded = useUnit($$ep.stores.$epAnswers.map(Boolean));
    const estatePlan = useUnit($$ep.stores.$epQInstance);
    const isInProgress = estatePlan?.status === 'In_Progress';
    const isComplete = estatePlan?.status === 'Complete';
    const estatePlanLoaded = useUnit($global.map((state) => state.temp?.estatePlanLoaded === true));
    const isInitialProductWaitingForRedirect = useUnit($$dashboard.$initialProduct);

    const isTrustFundingPageAvailable = useUnit($$trustFunding.$pageIsAvailable);

    if (!estatePlanLoaded || isInitialProductWaitingForRedirect) {
        return <LoadingPage />;
    }

    if (isInProgress) {
        return (
            <Routes>
                <Route path="/in-progress" element={<ProgressPage />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route
                    path="/trust-funding"
                    element={isTrustFundingPageAvailable ? <TrustFundingPage /> : <Navigate to="/dashboard/complete" />}
                />
                <Route path="/gift-trust" element={<GiftTrustPage />} />
                <Route path="*" element={<Navigate to="/dashboard/in-progress" />} />
            </Routes>
        );
    }

    if (isComplete) {
        if (!answersLoaded) {
            return <LoadingPage />;
        }
        return (
            <Routes>
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/complete" element={<CompletePage />} />
                <Route
                    path="/trust-funding"
                    element={isTrustFundingPageAvailable ? <TrustFundingPage /> : <Navigate to="/dashboard/complete" />}
                />
                <Route path="/gift-trust" element={<GiftTrustPage />} />
                <Route path="*" element={<Navigate to="/dashboard/complete" />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route path="/gift-trust" element={<GiftTrustPage />} />
            <Route
                path="/trust-funding"
                element={isTrustFundingPageAvailable ? <TrustFundingPage /> : <Navigate to="/dashboard/complete" />}
            />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/initial" element={<InitialPage />} />
            <Route path="/*" element={<Navigate to="/dashboard/initial" />} />
        </Routes>
    );
};

type DashboardPageProps = {
    children: React.ReactNode;
};

const DashboardPage = (props: DashboardPageProps) => {
    const { children } = props;
    const pathname = useUnit($pathname);
    const path = pathname.split(`/`).slice(1);

    const estatePlan = useUnit($$ep.stores.$epQInstance);
    const isInProgress = estatePlan?.status === 'In_Progress';
    const isComplete = estatePlan?.status === 'Complete';
    const isDashboardPage = useUnit($isDashboard);
    const assignedRoles = useUnit($global.map((state) => state.community?.assigned || null));
    const isToBeLinked = useUnit($global.map((state) => state.toBeLinked === true));
    const estatePlanLoaded = useUnit($global.map((state) => state.temp?.estatePlanLoaded === true));
    const answers = useUnit(
        $global.map((state) => state.answers?.[estatePlan?.questionnaire_instance_id || -1] || null),
    );
    const isUserHasAdvisor = !!useUnit($global.map((state) => state.community?.advisorData));

    const isFirstAssessmentGroupComplete = EstatePlanService.isFirstAssessmentGroupComplete();

    useEffect(() => {
        (async () => {
            if (estatePlan?.questionnaire_instance_id) {
                await $$api.answers.getAnswersFx({ id: estatePlan.questionnaire_instance_id });
            }
        })();
    }, [estatePlan?.questionnaire_instance_id]);

    useEffect(() => {
        if (!isInProgress) {
            return;
        }

        if (answers === null) {
            return;
        }

        if (!isFirstAssessmentGroupComplete && FeatureThresholdService.canShowAssessmentOnDashboard()) {
            return;
        }

        if (!path?.[1] && path?.[0] !== 'docs-generation') {
            navigateFx(`/dashboard/in-progress`);
        }
    }, [isInProgress, estatePlan, answers, path]);

    useEffect(() => {
        if (isDashboardPage && !path?.[1] && isComplete) {
            navigateFx(`/dashboard/complete`);
        }
    }, [path, isComplete]);

    useEffect(() => {
        const firstRoleParticipantId = _.map(assignedRoles, 'participant_id')?.[0];
        if (firstRoleParticipantId !== undefined && !estatePlan?.status && isToBeLinked) {
            navigateFx(`/community/assigned/${firstRoleParticipantId}`);
            return;
        }
    }, [JSON.stringify(estatePlan), estatePlanLoaded, JSON.stringify(assignedRoles), isToBeLinked]);

    useEffect(() => {
        document.body.classList.add('bg-white');
        return () => {
            document.body.classList.remove('bg-white');
        };
    }, []);

    if (!isBoolean(estatePlanLoaded) || !estatePlanLoaded) {
        return <LoadingPage />;
    }

    const hasHeader = ![
        '/',
        '/dashboard',
        '/dashboard/in-progress',
        '/dashboard/complete',
        '/dashboard/initial',
    ]?.includes?.(pathname);

    return (
        <>
            <UpdateParticipantModal />
            <DashboardScansModal />
            <ModalAssessment />

            <ViewDocumentsButton />
            <SecureButton />
            <ShippingDetailsCollectModal />

            <DashboardStyles.Wrapper>
                <DashboardStyles.MenuWrapper className="d-none d-lg-flex">
                    <DashboardStyles.MenuInnerWrapper>
                        <Menu />
                    </DashboardStyles.MenuInnerWrapper>
                    {!isUserHasAdvisor && <ReferalCard />}
                </DashboardStyles.MenuWrapper>
                <DashboardStyles.WorkspaceWrapper>
                    <DashboardStyles.WorkspaceInnerWrapper>
                        {hasHeader && <DashboardHeader />}
                        {children}
                    </DashboardStyles.WorkspaceInnerWrapper>
                </DashboardStyles.WorkspaceWrapper>
            </DashboardStyles.Wrapper>
        </>
    );
};

export const EPDashboard = () => {
    return (
        <DashboardPage>
            <FirstMenuBlockPages />
        </DashboardPage>
    );
};

export const CommunityDashboard = () => {
    return (
        <DashboardPage>
            <Routes>
                <Route path="/assigned/:key" element={<CommunityAssignedRolesPage />} />
                <Route path="/assigned" element={<CommunityAssignedRolesPage />} />
                <Route path="/advocates" element={<CommunityParticipantsPage />} />
                <Route path="/beneficiaries" element={<CommunityParticipantsPage />} />
                <Route path="/observers" element={<CommunityParticipantsPage />} />
                <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
        </DashboardPage>
    );
};

export default DashboardPage;
